import React, { useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, SubmitHandler, useFormContext } from "react-hook-form";
import {
  calculateLocationPrice,
  FormFields,
  generateTimeOptions,
} from "../../constants";
import { useTranslation } from "react-i18next";
import { LoadingButton } from "@mui/lab";
import moment from "moment";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DesktopTimePicker } from "@mui/x-date-pickers/DesktopTimePicker";
import {
  useGetKAFDLocations,
  useInitiateKAFDBookingMutation,
} from "src/api/kafd-prebooking";
import LocationsMap from "../map";
import downArrowIcon from "src/assets/svg-icons/down-arrow.svg";
import sarIcon from "src/assets/svg-icons/sar.svg";

const policyPoints = ["policyPoint1", "policyPoint2", "policyPoint3"];

const BookingForm: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [openDate, setOpenDate] = useState(false);
  const [openArrivingTime, setOpenArrivingTime] = useState(false);
  const [sameAsGuest, setSameAsGuest] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<any>();
  const [price, setPrice] = useState(0);
  const [extended, setExtended] = useState(false);

  const { workingFrom, workingTo, hasGlobalDiscount, globalDiscountPer } =
    selectedLocation || {};

  // APIS
  const { mutate, status, data, isPending } = useInitiateKAFDBookingMutation();

  const { data: locationsData } = useGetKAFDLocations();

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    getValues,
    watch,
    clearErrors,
    trigger,
    setError,
  } = useFormContext<FormFields>();

  const leavingTime = moment(watch("arrivingTime"))
    .add(watch("stayingTime"), "hours")
    .toDate();

  const invalidArrivingDate = !moment(watch("arrivingDate")).isSameOrAfter(
    moment().startOf("day")
  );
  const invalidArrivingTime =
    moment(watch("arrivingDate")).isSame(moment(), "day") &&
    moment(watch("arrivingTime")).isBefore(moment());

  console.log({ invalidArrivingDate, invalidArrivingTime });

  const maxTime = {
    hour:
      workingTo?.hour < workingFrom?.hour
        ? workingTo?.hour - 2 + 24
        : workingTo?.hour - 2,
    minute: workingTo?.minute ?? 0,
  };

  const validateDateTimeInPast = (arrivingDate: Date, arrivingTime: Date) => {
    // Combine the date and time into a single moment object
    const arrivingDateTime = moment(arrivingDate).set({
      hour: arrivingTime.getHours(),
      minute: arrivingTime.getMinutes(),
      second: arrivingTime.getSeconds(),
      millisecond: arrivingTime.getMilliseconds(),
    });

    // Get the current date and time
    const now = moment();

    // Check if the arriving date and time is in the past
    return arrivingDateTime.isBefore(now);
  };

  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    console.log({ data });
    if (!data.arrivingTime) {
      return;
    }

    const hourSelected = arrivingTime?.getHours() ?? 0;
    const minuteSelected = arrivingTime?.getMinutes() ?? 0;

    const isTooLate =
      hourSelected > maxTime.hour ||
      (hourSelected === maxTime.hour && minuteSelected > maxTime.minute);

    if (isTooLate) {
      setError("arrivingTime", {
        type: "custom",
        message: `${t("maxTimeAllowed")} ${maxTime.hour}:${maxTime.minute}`,
      });
      return;
    } else {
      clearErrors("arrivingTime");
    }

    if (validateDateTimeInPast(data.arrivingDate as Date, data.arrivingTime)) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: t("canNotPickPrevTime"),
        },
      ]);
      return;
    }

    if (data.arrivingTime && leavingTime <= data.arrivingTime) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: t("leavingTimeMustAfterArrivalTime"),
        },
      ]);
      return;
    }

    const arrivingDateTime = moment(data.arrivingDate).set({
      hour: data.arrivingTime.getHours(),
      minute: data.arrivingTime.getMinutes(),
      second: 0,
      millisecond: 0,
    });

    const leavingDateTime = moment(data.arrivingDate).set({
      hour: leavingTime.getHours(),
      minute: leavingTime.getMinutes(),
      second: 0,
      millisecond: 0,
    });

    mutate({
      locationId: data.location,
      hostName: data.hostName,
      hostIsoCode: "SA",
      hostCountryCode: "966",
      hostMobileNumber: data.hostNumber,
      customerName: data.guestName,
      customerIsoCode: "SA",
      customerCountryCode: "966",
      customerMobileNumber: data.guestNumber,
      arrivingTime: arrivingDateTime.valueOf(),
      leavingTime: leavingDateTime.valueOf(),
    });
  };

  const handleOpenDatePicker = () => {
    setOpenDate(true);
  };

  const handleCloseDatePicker = () => {
    setOpenDate(false);
  };

  const handleOpenArrivingTimePicker = () => {
    setOpenArrivingTime(true);
  };

  const handleCloseArrivingTimePicker = () => {
    setOpenArrivingTime(false);
  };

  const afterDiscountPercentageRatio = hasGlobalDiscount
    ? (100 - globalDiscountPer) / 100
    : 1;

  useEffect(() => {
    if (selectedLocation && watch("arrivingTime") && leavingTime) {
      const price = calculateLocationPrice({
        startTime: getValues("arrivingTime"),
        endTime: leavingTime,
        service: selectedLocation.services[0],
      });
      setPrice(price * afterDiscountPercentageRatio);
    }
  }, [selectedLocation, watch("arrivingTime"), leavingTime]);

  useEffect(() => {
    if (getValues("location")) {
      const location = locationsData?.data.find((loc: any) => {
        return loc.locationId === getValues("location");
      });
      setSelectedLocation(location);
    }
    setValue("arrivingDate", undefined);
    setValue("arrivingTime", undefined);
    setValue("stayingTime", "");
  }, [watch("location")]);

  const arrivingTime = watch("arrivingTime");

  useEffect(() => {
    setValue("stayingTime", "");
  }, [arrivingTime]);

  useEffect(() => {
    if (status === "success") {
      window.location.href = data?.data.paymentUrl;
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: t("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  useEffect(() => {
    if (sameAsGuest) {
      setValue("guestName", getValues("hostName"));
      setValue("guestNumber", getValues("hostNumber"));
      clearErrors(["guestName", "guestNumber"]); // Clear errors for these fields
    }
  }, [sameAsGuest, watch("hostName"), watch("hostNumber")]);

  useEffect(() => {
    if (watch("arrivingTime")) {
      trigger(["arrivingTime"]);
    }
  }, [watch("arrivingDate")]);

  const priceToDisplay =
    invalidArrivingDate || invalidArrivingTime || !watch("stayingTime")
      ? 0
      : price ?? 0;

  const startPointOfBookingTime = {
    hour: watch("arrivingTime")
      ? watch("arrivingTime")?.getHours()
      : workingFrom?.hour ?? 0,
    minute: watch("arrivingTime")
      ? watch("arrivingTime")?.getMinutes()
      : workingFrom?.minute ?? 0,
  };

  const options = generateTimeOptions(
    startPointOfBookingTime,
    workingTo ?? { hour: 0, minute: 0 }
  );

  console.log({ errors });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box overflow={"hidden"} borderRadius={"12px"}>
        <Box bgcolor={"#272727"} padding={{ xs: "30px 16px", md: "30px 47px" }}>
          <Typography color={"primary"} fontSize={"24px"} fontWeight={500}>
            {t("bookingInfo")}
          </Typography>
          <Typography mb={3}>{t("fillDetailsBelow")}</Typography>
          <Grid container spacing={3}>
            <Grid
              item
              xs={12}
              md={8}
              display={"flex"}
              flexDirection={"column"}
              gap={3}
            >
              <Box>
                <Typography sx={{ mb: "10px" }}>
                  {t("selectLocation")}
                </Typography>
                <FormControl fullWidth error={!!errors.location} size="medium">
                  <Controller
                    name="location"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        displayEmpty
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        sx={{ bgcolor: "#222" }}
                      >
                        <MenuItem value="" disabled>
                          {t("selectLocation")}
                        </MenuItem>
                        {locationsData?.data.map((location: any) => {
                          return (
                            <MenuItem
                              key={location.locationId}
                              value={location.locationId}
                            >
                              {i18n.language === "en"
                                ? location.locationName
                                : location.locationNameAr}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    )}
                  />
                  <FormHelperText id="my-helper-text">
                    {t(`${errors.location?.message ?? ""}`)}
                  </FormHelperText>
                </FormControl>
              </Box>

              <Box>
                <Typography sx={{ mb: "10px" }}>{t("arrivingDate")}</Typography>
                <div>
                  <Controller
                    name="arrivingDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!errors.arrivingDate,
                            helperText: t(
                              `${errors.arrivingDate?.message ?? ""}`
                            ),
                            onClick: handleOpenDatePicker,
                            onBlur: () => {
                              if (!openDate) {
                                field.onBlur();
                              }
                            },
                            inputProps: { readOnly: true },
                          },
                        }}
                        format={"dd/MM/yyyy"}
                        open={openDate}
                        onOpen={handleOpenDatePicker}
                        onClose={handleCloseDatePicker}
                        minDate={moment().toDate()} // Disable days before today
                        maxDate={moment().endOf("month").toDate()} // Disable days after end of month
                        shouldDisableDate={(date) =>
                          (selectedLocation?.daysOff ?? []).includes(
                            date.getDay()
                          )
                        }
                        sx={{ bgcolor: "#222" }}
                        disabled={!selectedLocation}
                      />
                    )}
                  />
                </div>
              </Box>

              <Stack direction={{ xs: "column", sm: "row" }} gap={3}>
                <Box width={"100%"}>
                  <Typography sx={{ mb: "10px" }}>
                    {t("arrivingTime")}
                  </Typography>
                  <div>
                    <Controller
                      name="arrivingTime"
                      control={control}
                      render={({ field }) => (
                        <DesktopTimePicker
                          {...field}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!errors.arrivingTime,
                              helperText: t(
                                `${errors.arrivingTime?.message ?? ""}`
                              ),
                              onClick: handleOpenArrivingTimePicker,
                              onBlur: () => {
                                if (!openArrivingTime) {
                                  field.onBlur();
                                }
                              },
                              inputProps: { readOnly: true },
                            },
                          }}
                          ampm={false}
                          format={"HH:mm"} //"HH:mm"
                          open={openArrivingTime}
                          onOpen={handleOpenArrivingTimePicker}
                          onClose={handleCloseArrivingTimePicker}
                          minTime={
                            new Date(
                              0,
                              0,
                              0,
                              workingFrom?.hour ?? 0,
                              workingFrom?.minute ?? 0
                            )
                          }
                          maxTime={
                            new Date(0, 0, 0, maxTime.hour, maxTime.minute)
                          }
                          // shouldDisableTime={(value, clockType) => {
                          //   if (!value) return false;
                          //   const hour = value.getHours();
                          //   if (clockType === "hours") {
                          //     return hour < 9 || hour > 17;
                          //   }
                          //   return false;
                          // }}
                          sx={{ bgcolor: "#222" }}
                          disabled={!selectedLocation}
                        />
                      )}
                    />
                  </div>
                </Box>

                <Box width={"100%"}>
                  <Typography sx={{ mb: "10px" }}>{t("stayFor")}</Typography>
                  <FormControl
                    fullWidth
                    error={!!errors.stayingTime}
                    size="medium"
                  >
                    <Controller
                      name="stayingTime"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          value={field.value ?? ""}
                          displayEmpty
                          labelId="stayingTime-select-label"
                          id="stayingTime-simple-select"
                          sx={{ bgcolor: "#222" }}
                          disabled={!selectedLocation}
                        >
                          <MenuItem value={""} disabled>
                            {t("stayFor")}
                          </MenuItem>
                          {options?.map((option) => {
                            return (
                              <MenuItem key={option} value={option}>
                                {option} {t(option > 1 ? "hours" : "hour")}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      )}
                    />
                    <FormHelperText id="my-helper-text">
                      {t(`${errors.stayingTime?.message ?? ""}`)}
                    </FormHelperText>
                  </FormControl>
                </Box>
              </Stack>
              {/* price */}
              <Box>
                <Typography mb={"10px"}>{t("valetServiceFees")}</Typography>
                <Box
                  display={"flex"}
                  flexDirection={"row"}
                  gap={"4px"}
                  bgcolor={"#38383880"}
                  p={"20px"}
                  borderRadius={"12px"}
                >
                  <Typography>{t("price")}: </Typography>
                  <Stack
                    direction={i18n.language === "en" ? "row" : "row-reverse"}
                    alignItems={"center"}
                    gap={"4px"}
                  >
                    <img height={13} src={sarIcon} alt="" />
                    <Typography fontSize={"17px"}>{priceToDisplay}</Typography>
                  </Stack>
                  {/* {hasGlobalDiscount && priceToDisplay > 0 && (
                    <Typography color={"#4EBE86"}>
                      ( %{globalDiscountPer} {t("discountApplied")} )
                    </Typography>
                  )} */}
                </Box>
                <Typography color={"primary"} mt={"10px"}>
                  {t("PaidFeeNotRefundableAndExtraFeesMightApplied")}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4} order={{ xs: -1, md: 0 }}>
              <LocationsMap locations={locationsData?.data || []} />
            </Grid>
          </Grid>

          {/* divider */}
          <Box bgcolor={"#383838"} width={"100%"} height={"1px"} my={4} />

          <Typography color={"primary"} fontSize={"24px"} fontWeight={500}>
            {t("hostGuestInfo")}
          </Typography>
          <Typography mb={3}>{t("fillDetailsBelow")}</Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box>
                <Typography sx={{ mb: "10px" }}>{t("hostName")}</Typography>
                <Controller
                  name="hostName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      placeholder={t("name")}
                      {...field}
                      error={!!errors.hostName}
                      helperText={t(`${errors.hostName?.message ?? ""}`)}
                      fullWidth
                      size="medium"
                      variant="outlined"
                      sx={{ bgcolor: "#222" }}
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box>
                <Typography sx={{ mb: "10px" }}>{t("hostMobile")}</Typography>
                <div dir="ltr">
                  <Controller
                    name="hostNumber"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        placeholder={t("mobileNumber")}
                        {...field}
                        error={!!errors.hostNumber}
                        helperText={t(`${errors.hostNumber?.message ?? ""}`)}
                        fullWidth
                        size="medium"
                        variant="outlined"
                        sx={{ bgcolor: "#222" }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography
                                sx={{
                                  direction:
                                    i18n.language === "en" ? "ltr" : "rtl",
                                }}
                              >
                                +966
                              </Typography>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </div>
              </Box>
            </Grid>
            <Grid item xs={12} md={4} display={"flex"} alignItems={"center"}>
              <Stack pt={3} pl={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      defaultChecked={sameAsGuest}
                      onChange={(e) => {
                        setSameAsGuest(e.target.checked);
                      }}
                    />
                  }
                  label={t("GuestInfoTheSame")}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box>
                <Typography sx={{ mb: "10px" }}>{t("guestName")}</Typography>
                <Controller
                  name="guestName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      placeholder={t("name")}
                      {...field}
                      error={!!errors.guestName}
                      helperText={t(`${errors.guestName?.message ?? ""}`)}
                      fullWidth
                      size="medium"
                      variant="outlined"
                      sx={{ bgcolor: "#222" }}
                      disabled={sameAsGuest}
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box>
                <Typography sx={{ mb: "10px" }}>{t("guestMobile")}</Typography>
                <div dir="ltr">
                  <Controller
                    name="guestNumber"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        placeholder={t("mobileNumber")}
                        {...field}
                        error={!!errors.guestNumber}
                        helperText={t(`${errors.guestNumber?.message ?? ""}`)}
                        fullWidth
                        size="medium"
                        variant="outlined"
                        sx={{ bgcolor: "#222" }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography
                                sx={{
                                  direction:
                                    i18n.language === "en" ? "ltr" : "rtl",
                                }}
                              >
                                +966
                              </Typography>
                            </InputAdornment>
                          ),
                        }}
                        disabled={sameAsGuest}
                      />
                    )}
                  />
                </div>
              </Box>
            </Grid>
          </Grid>

          <Stack
            direction={"row"}
            alignItems={"center"}
            mt={3}
            mb={2}
            gap={4}
            sx={{ cursor: "pointer" }}
            onClick={() => setExtended((prev) => !prev)}
          >
            <Typography color={"primary"} fontSize={"20px"} fontWeight={500}>
              {t("usagePolicy")}
            </Typography>
            <Box sx={{ transform: extended ? "scaleY(-1)" : "unset" }}>
              <img src={downArrowIcon} alt="" />
            </Box>
          </Stack>

          {extended &&
            policyPoints.map((point) => (
              <Stack key={point} direction={"row"} gap={1.2} mb={0.4}>
                <Box
                  width={"5px"}
                  height={"5px"}
                  bgcolor={"#D0D0D0"}
                  mt={"10px"}
                />
                <Typography color={"#D0D0D0"} lineHeight={"26px"}>
                  {t(point)}
                </Typography>
              </Stack>
            ))}

          <Stack mt={"62px"}>
            <LoadingButton
              size="large"
              variant="contained"
              sx={{
                mx: "auto",
                minWidth: { xs: "100%", sm: "310px" },
                fontSize: "16px",
              }}
              type="submit"
              loading={isPending || !!data}
            >
              {t("bookNow")}
            </LoadingButton>
          </Stack>
        </Box>
      </Box>
    </form>
  );
};

export default BookingForm;
